import { PriceGroup } from '@adapters/bigcommerce/types/product';
import { ALGOLIA_CHECKOUT_QUERY_ID_NAME } from '@adapters/bigcommerce/constants';
import Cookies from 'js-cookie';
import aa from 'search-insights';
import { VariantPrice } from '@context/SaveForLaterContext/types';

export const initAlgoliaAnalytics = () => {
  aa('init', {
    partial: true,
    useCookie: window.OnetrustActiveGroups?.includes('C0002') ? true : false,
    appId: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
    apiKey: process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY || '',
  });
};

export const getIndex = (locale?: string, append?: string) => {
  const indexName =
    process.env.NEXT_PUBLIC_ALGOLIA_INDEX_OVERRIDE ?? 'products_';
  return `${indexName}${locale}${append ? append : ''}`;
};

// userToken is a required field for Algolia analytics
// if we don't already have one, we can generate one
export const getAlgoliaUserToken = async () => {
  initAlgoliaAnalytics();
  const algoliaCookieName = '_ALGOLIA';
  const userTokenCookie = Cookies.get(algoliaCookieName);

  if (userTokenCookie) {
    return userTokenCookie;
  }

  const algoliaUserToken = await new Promise<string>((resolve, reject) => {
    aa('getUserToken', {}, (err, userToken) => {
      if (err) {
        console.error('Error generating algolia user token:', err);
        reject(err);
      } else {
        if (userToken) {
          aa('setUserToken', userToken);
        }
        resolve(userToken as string);
      }
    });
  });

  return algoliaUserToken;
};

interface TrackAlgoliaAddToCartProps {
  variantDataForAlgolia: {
    sku: string;
    price: VariantPrice | PriceGroup | null;
  };
  quantity: number;
  locale?: string;
}

export const trackAlgoliaAddToCart = async ({
  variantDataForAlgolia,
  quantity,
  locale,
}: TrackAlgoliaAddToCartProps) => {
  const { sku, price } = variantDataForAlgolia;

  initAlgoliaAnalytics();
  const algoliaUserToken = await getAlgoliaUserToken();
  const algoliaQueryId = Cookies.get(ALGOLIA_CHECKOUT_QUERY_ID_NAME) ?? null;
  const indexName = getIndex(locale);
  const eventType = algoliaQueryId
    ? 'addedToCartObjectIDsAfterSearch'
    : 'addedToCartObjectIDs';
  const eventName = algoliaQueryId
    ? 'Product Added To Cart After Search'
    : 'Product Added To Cart';

  const discount =
    price?.basePrice?.value && price?.price?.value
      ? parseFloat((price.basePrice.value - price.price.value).toFixed(2))
      : 0;

  const eventValue =
    (price?.price?.value && price?.price?.value * quantity) ?? null;

  try {
    if (algoliaUserToken) {
      aa(eventType, {
        eventName,
        userToken: algoliaUserToken,
        index: indexName,
        objectIDs: [sku || ''],
        objectData: [
          discount
            ? {
                price: price?.price?.value,
                quantity: quantity,
                discount,
              }
            : {
                price: price?.price?.value,
                quantity: quantity,
              },
        ],
        currency: 'CAD',
        ...(algoliaQueryId && { queryID: algoliaQueryId }),
        ...(eventValue && { value: eventValue.toFixed(2) }),
      });
    }
  } catch (error) {
    console.error(`Error trying to send ${eventType} to Algolia`, error);
  }
};
